import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BrandedFooter } from "./BrandedFooter";

import {
  completeSpeedtest,
  runSpeedtest,
  simulateSpeedtest,
  startSpeedtest,
  type SpeedtestProgress,
  type SpeedtestResult,
} from "../api/portal";

interface SpeedtestPageProps {
  portalToken: string;
  siteName: string;
  onComplete: (result: SpeedtestResult) => void;
  onSkip: () => void;
}

type Phase = "idle" | "measuring" | "done" | "error";

export function SpeedtestPage({
  portalToken,
  siteName,
  onComplete,
  onSkip,
}: SpeedtestPageProps) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>("idle");
  const [result, setResult] = useState<SpeedtestResult | null>(null);

  // Live progress state (prepared for Task #8 visualisation)
  const [measurePhase, setMeasurePhase] = useState<SpeedtestProgress["phase"] | null>(null);
  const [currentMbps, setCurrentMbps] = useState<number>(0);
  const [stageName, setStageName] = useState<string>("");

  const testMatch = new URLSearchParams(window.location.search).has("testmatch");

  async function handleStart() {
    setPhase("measuring");
    setMeasurePhase(null);
    setCurrentMbps(0);
    setStageName("");

    try {
      let res: SpeedtestResult;

      if (testMatch) {
        res = await simulateSpeedtest(portalToken);
      } else {
        const session = await startSpeedtest(portalToken);

        const measurement = await runSpeedtest(portalToken, (p) => {
          setMeasurePhase(p.phase);
          setCurrentMbps(p.currentMbps);
          setStageName(p.stageName);
        });

        const apiRes = await completeSpeedtest(portalToken, session.correlation_token, {
          download_mbps: measurement.downloadMbps,
          upload_mbps: measurement.uploadMbps,
          ping_ms: measurement.pingMs,
          jitter_ms: measurement.jitterMs,
          total_bytes_downloaded: measurement.totalBytesDownloaded,
        });

        res = {
          ...apiRes,
          speedtest_token: session.correlation_token,
        };

        if (!session.correlation_supported) {
          // Correlation not possible for this site – skip DeviceConfirm/IpConfirm
          setResult(res);
          setPhase("done");
          onComplete(res);
          return;
        }
      }

      setResult(res);
      setPhase("done");
    } catch {
      setPhase("error");
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-md mx-auto px-4 py-8">
        <h1 className="text-xl font-semibold text-gray-800 mb-2">{siteName}</h1>
        <h2 className="text-lg font-medium text-gray-700 mb-2">
          {t("speedtest_title")}
        </h2>
        <p className="text-gray-500 text-sm mb-6">{t("speedtest_intro")}</p>

        {phase === "done" && result ? (
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-base font-semibold text-gray-800 mb-4">
                {t("speedtest_result_title")}
              </h3>

              {result.correlation_status === "matched" && (
                <div className="inline-block bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full mb-4">
                  {t("speedtest_matched")}
                </div>
              )}
              {result.correlation_status === "no_match" && (
                <p className="text-gray-500 text-sm mb-4">
                  {t("speedtest_no_match")}
                </p>
              )}

              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-xs text-gray-500 mb-1">{t("speedtest_download")}</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {result.download_mbps != null ? `${result.download_mbps.toFixed(1)} Mbps` : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">{t("speedtest_upload")}</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {result.upload_mbps != null ? `${result.upload_mbps.toFixed(1)} Mbps` : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">{t("speedtest_ping")}</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {result.ping_ms != null ? `${result.ping_ms.toFixed(0)} ms` : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">{t("speedtest_jitter")}</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {result.jitter_ms != null ? `${result.jitter_ms.toFixed(0)} ms` : "—"}
                  </p>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => onComplete(result)}
              className="w-full py-3 px-4 btn-primary rounded-xl font-medium transition-colors"
            >
              {t("speedtest_continue")}
            </button>
          </div>
        ) : phase === "measuring" ? (
          <div className="text-center py-8" aria-live="polite">
            {/* Phase indicator: Download → Upload → Ping */}
            <div className="flex items-center justify-center gap-1 mb-6 text-xs font-medium">
              <span className={`px-2 py-0.5 rounded-full ${
                measurePhase === null || measurePhase === "download"
                  ? "bg-blue-100 text-blue-700"
                  : "text-blue-400"
              }`}>
                {t("speedtest_phase_download")}
              </span>
              <span className="text-gray-300">→</span>
              <span className={`px-2 py-0.5 rounded-full ${
                measurePhase === "upload"
                  ? "bg-blue-100 text-blue-700"
                  : measurePhase === "ping"
                  ? "text-blue-400"
                  : "text-gray-400"
              }`}>
                {t("speedtest_phase_upload")}
              </span>
              <span className="text-gray-300">→</span>
              <span className={`px-2 py-0.5 rounded-full ${
                measurePhase === "ping" ? "bg-blue-100 text-blue-700" : "text-gray-400"
              }`}>
                {t("speedtest_phase_ping")}
              </span>
            </div>

            {/* Live Mbps counter */}
            {(measurePhase === "download" || measurePhase === "upload") && currentMbps > 0 ? (
              <div className="mb-4">
                <p className="text-3xl font-bold text-gray-800 tabular-nums">
                  {currentMbps.toFixed(1)}
                </p>
                <p className="text-sm text-gray-500 mt-1">Mbps · {stageName}</p>
              </div>
            ) : (
              <div className="flex justify-center mb-4">
                <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            <p className="text-gray-500 text-sm">{t("speedtest_running")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {phase === "error" && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-2">
                <p className="text-red-700 text-sm">{t("speedtest_error")}</p>
              </div>
            )}
            <button
              type="button"
              onClick={handleStart}
              className="w-full py-3 px-4 btn-primary rounded-xl font-medium transition-colors"
            >
              {phase === "error" ? t("speedtest_retry_btn") : t("speedtest_start_btn")}
            </button>
            {phase === "error" && (
              <button
                type="button"
                onClick={onSkip}
                className="w-full py-3 px-4 bg-white border border-gray-300 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-colors"
              >
                {t("speedtest_skip")}
              </button>
            )}
          </div>
        )}

        <BrandedFooter />
      </div>
    </div>
  );
}
